import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService, hrErrorMessage } from '../hr.service';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { Store, User } from '@app/shared/_models/common';
import {
	HrAbsence, HrEmployee, HrMeal, hrEmployeeNameOf, hrIsHrManager, hrMealKindLabel, hrMonthLabel, hrPeriodLabel,
} from '@app/shared/_models/hr';
import { EVENTTYP_DELETE, EVENTTYP_INSERT, OBJECTTYP_HR_MEAL } from '@app/shared/constants';
import { PayrollRules, hrPayrollRulesOf } from '../hr-payroll';
import {
	HR_MEAL_REASON_LABELS, MealVoucherBeneficiary, MealVoucherDay, MealVoucherLine, MealVoucherReport,
	buildMealVoucherCsv, buildMealVoucherDetailCsv, buildMealVouchers, buildSwileCsv, euros,
	mealVoucherSharesOf, swileGaps,
} from '../hr-meal-voucher';
import { addDays, effectiveSlotsForRange, hoursLabel, minutesLabel } from '../hr-schedule-engine';
import { pairClocks } from '../hr-timesheet';
import { todayStr } from '@app/shared/time';
import { MealDialogComponent, MealDialogData, MealDialogEmployee, MealDialogResult } from './meal-dialog/meal-dialog.component';

// DÉCOMPTE DES TITRES-RESTAURANT — /hr/meals.
//
// L'écran de fin de mois : combien de titres commander, pour qui, et combien retenir sur chaque
// bulletin. Le rapport de paie en donne le total dans une colonne ; ici on peut l'OUVRIR — jour
// par jour, avec le motif de chaque journée qui n'a pas donné de titre.
//
// C'est aussi ici que se saisissent les REPAS PAYÉS PAR L'ENTREPRISE : la formation avec déjeuner,
// la note de frais, le repas d'affaires. Chacun retire le titre de son jour, et le détail dit
// lequel — parce que la question « pourquoi je n'ai que 17 tickets ? » se pose vraiment, et qu'y
// répondre en rouvrant le planning de tout un mois n'est pas une réponse.
//
// L'écran ne CALCULE rien lui-même : tout vient du moteur pur hr-meal-voucher.ts, celui-là même
// qu'utilise la préparation de paie. Deux écrans, un seul chiffre.
@Component({
	selector: 'app-hr-meals',
	standalone: true,
	imports: [RouterLink, MenuCommonComponent, ReactiveFormsModule, MatFormFieldModule, MatSelectModule,
		MatButtonModule, MatIconModule, MatTooltipModule, MatDialogModule],
	templateUrl: './meals.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './meals.component.scss',
})
export class MealsComponent implements OnInit {

	public hrService = inject(HrService);
	private commonService = inject(CommonService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);
	private dialog = inject(MatDialog);

	public from = '';
	public to = '';
	public periodLabel = '';
	public periodRange = '';
	public loading = false;
	public isAdmin = false;

	public rules: PayrollRules = hrPayrollRulesOf(null);
	public report: MealVoucherReport | null = null;
	public stores: Store[] = [];
	public storeCtrl = new FormControl<number | null>(null);
	private storeDefaulted = false;

	// Le salarié dont le détail jour par jour est ouvert (un seul à la fois : le détail est long,
	// et deux ouverts en même temps ne se comparent pas mieux).
	public openEmployee: number | null = null;

	private employees: HrEmployee[] = [];
	private users: User[] = [];
	private meals: HrMeal[] = [];
	private workingDays = new Map<number, Set<string>>();		// jours où chacun a au moins un créneau

	ngOnInit(): void {
		this.storeCtrl.valueChanges.subscribe(() => this.load());
		this.setMonth(todayStr());
	}

	// ============================================================
	// Période
	// ============================================================

	private setMonth(dat: string): void {
		const first = dat.slice(0, 8) + '01';
		const nextMonth = addDays(first, 32).slice(0, 8) + '01';
		this.from = first;
		this.to = addDays(nextMonth, -1);
		this.load();
	}

	onPrevMonth(): void { this.setMonth(addDays(this.from, -1)); }
	onNextMonth(): void { this.setMonth(addDays(this.to, 1)); }
	onToday(): void { this.setMonth(todayStr()); }

	// ============================================================
	// Chargement
	// ============================================================

	private load(): void {
		if (!this.from || !this.to) return;
		this.loading = true;

		forkJoin({
			employees: this.hrService.getEmployees$(),
			users: this.hrService.getUsers$(),
			stores: this.commonService.getStores(),
			schedules: this.hrService.getSchedules$(),
			absences: this.hrService.getAbsences$(this.from, this.to),
			overrides: this.hrService.getOverrides$(this.from, this.to),
			clocks: this.hrService.getClocks$(this.from, this.to),
			validations: this.hrService.getValidations$(this.from, this.to),
			meals: this.hrService.getMeals$(this.from, this.to),
			payrollRules: this.hrService.payrollRules$(),
		}).subscribe(({ employees, users, stores, schedules, absences, overrides, clocks, validations, meals, payrollRules }) => {
			this.users = users;
			this.stores = stores;
			this.isAdmin = hrIsHrManager(this.loginService.loggeduser, users);
			this.rules = payrollRules.rules;
			this.meals = meals;

			if (!this.storeDefaulted) {
				this.storeDefaulted = true;
				this.storeCtrl.setValue(this.loginService.currentstation?.store ?? null, { emitEvent: false });
			}
			const store = this.storeCtrl.value;
			this.employees = employees.filter(e => e.is_active !== false
				&& (store == null || e.id_store === store || (e.mobility ?? []).includes(store)));

			const slots = effectiveSlotsForRange(this.from, this.to, schedules, overrides, absences as HrAbsence[]);
			const { periods } = pairClocks(clocks, todayStr());
			const names = new Map(this.employees.map(e => [e.id, hrEmployeeNameOf(e, users)]));

			this.workingDays = new Map();
			for (const s of slots) {
				if (s.absent) continue;
				const set = this.workingDays.get(s.id_employee) ?? new Set<string>();
				set.add(s.dat);
				this.workingDays.set(s.id_employee, set);
			}

			this.report = buildMealVouchers({
				employees: this.employees, names, rules: this.rules.meal_vouchers,
				slots, meals, from: this.from, to: this.to,
				periods, time_source: this.rules.time_source,
				validations: validations.map(v => ({
					id_employee: v.id_employee, dat: v.dat, paid_min: v.paid_min, absent: v.absent,
				})),
			});

			this.periodLabel = hrMonthLabel(this.from);
			this.periodRange = hrPeriodLabel(this.from, this.to);
			this.loading = false;
		});
	}

	// ============================================================
	// Affichage
	// ============================================================

	get enabled(): boolean { return this.rules.meal_vouchers.enabled; }
	get lines(): MealVoucherLine[] { return this.report?.lines ?? []; }
	get warnings() { return this.report?.warnings ?? []; }

	// Les lignes à MONTRER : celles qui ont quelque chose à dire. Un salarié exclu des titres et
	// sans aucun repas d'entreprise n'apporte rien au tableau — il apparaît dans le pied de page.
	get shownLines(): MealVoucherLine[] {
		return this.lines.filter(l => l.eligible || l.company_meals_total > 0);
	}

	get excludedCount(): number {
		return this.lines.filter(l => !l.eligible).length;
	}

	get faceValue(): string { return euros(this.rules.meal_vouchers.face_value); }
	get employerShare(): string { return euros(mealVoucherSharesOf(this.rules.meal_vouchers).employer); }
	get employeeShare(): string { return euros(mealVoucherSharesOf(this.rules.meal_vouchers).employee); }
	get pivotLabel(): string { return minutesLabel(this.rules.meal_vouchers.pivot_min); }

	get sourceLabel(): string {
		return this.rules.time_source === 'clock' ? 'Horaires badgés' : 'Horaires du planning';
	}

	euros(n: number): string { return euros(n); }
	hours(min: number): string { return hoursLabel(min); }
	kindLabel(kind: number | null): string { return hrMealKindLabel(kind); }
	reasonLabel(d: MealVoucherDay): string { return HR_MEAL_REASON_LABELS[d.reason]; }

	// « Lun. 13/07 » — le jour de semaine compte ici : c'est lui qui explique les journées coupées.
	dayLabel(dat: string): string {
		const wd = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'][new Date(dat + 'T12:00:00').getDay()];
		return wd + ' ' + dat.slice(8, 10) + '/' + dat.slice(5, 7);
	}

	// « 08:30 → 18:00 », ou rien du tout les jours sans horaire.
	rangeLabel(d: MealVoucherDay): string {
		if (d.start_min === null || d.end_min === null) return '—';
		return minutesLabel(d.start_min) + ' → ' + minutesLabel(d.end_min);
	}

	toggleDetail(line: MealVoucherLine): void {
		this.openEmployee = this.openEmployee === line.id_employee ? null : line.id_employee;
	}

	// ============================================================
	// Repas d'entreprise — saisie
	// ============================================================

	// Saisie en lot depuis l'en-tête : « formation les 12 et 13 pour trois personnes ».
	onAddMeals(): void {
		if (!this.isAdmin) return;
		const start = this.from > todayStr() ? this.from : (todayStr() <= this.to ? todayStr() : this.to);
		const data: MealDialogData = {
			datestart: start,
			dateend: start,
			min: this.from,
			max: this.to,
			preselected: [],
			employees: this.employeesForDialog(start, start),
		};
		this.dialog.open(MealDialogComponent, { data, width: '640px', autoFocus: false })
			.afterClosed().subscribe((r: MealDialogResult | undefined) => {
				if (r) this.saveMeals(r);
			});
	}

	// Saisie depuis UNE ligne de détail : le geste le plus direct — on voit la journée qui pose
	// question, on dit sur place que l'entreprise a payé le repas.
	onDayMeal(line: MealVoucherLine, day: MealVoucherDay): void {
		if (!this.isAdmin) return;
		if (day.id_meal) { this.onRemoveMeal(line, day); return; }
		const data: MealDialogData = {
			datestart: day.dat,
			dateend: day.dat,
			min: this.from,
			max: this.to,
			preselected: [line.id_employee],
			employees: this.employeesForDialog(day.dat, day.dat),
		};
		this.dialog.open(MealDialogComponent, { data, width: '640px', autoFocus: false })
			.afterClosed().subscribe((r: MealDialogResult | undefined) => {
				if (r) this.saveMeals(r);
			});
	}

	private employeesForDialog(from: string, to: string): MealDialogEmployee[] {
		const days: string[] = [];
		for (let d = from; d <= to; d = addDays(d, 1)) days.push(d);
		return this.employees.map(e => ({
			id: e.id,
			name: hrEmployeeNameOf(e, this.users),
			working: days.some(d => this.workingDays.get(e.id)?.has(d)),
			already: this.meals.some(m => m.id_employee === e.id && days.includes(m.dat)),
		}));
	}

	private saveMeals(r: MealDialogResult): void {
		const payload = this.hrService.buildMealPayload(r);
		this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_MEAL, payload).subscribe({
			next: (res) => {
				const n = res?.data?.count ?? 0;
				this.alertService.success(n > 1 ? (n + ' repas enregistrés') : 'Repas enregistré');
				this.load();
			},
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	onRemoveMeal(line: MealVoucherLine, day: MealVoucherDay): void {
		if (!this.isAdmin || !day.id_meal) return;
		Swal.fire({
			title: 'Retirer ce repas ?',
			html: '<b>' + line.name + '</b> — ' + this.dayLabel(day.dat) + '<br>'
				+ this.kindLabel(day.meal_kind)
				+ '<br><br>La journée redeviendra éligible au titre-restaurant si son horaire le permet.',
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: 'Retirer',
			cancelButtonText: 'Annuler',
		}).then(res => {
			if (!res.isConfirmed) return;
			this.hrService.postHrEvent(EVENTTYP_DELETE, OBJECTTYP_HR_MEAL, { id: day.id_meal }).subscribe({
				next: () => { this.alertService.success('Repas retiré'); this.load(); },
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}

	// ============================================================
	// Exports
	// ============================================================

	onExport(): void {
		if (!this.report) return;
		this.download(buildMealVoucherCsv(this.report), 'titres-restaurant-' + this.from.slice(0, 7) + '.csv');
	}

	onExportDetail(): void {
		if (!this.report) return;
		this.download(buildMealVoucherDetailCsv(this.report), 'titres-restaurant-detail-' + this.from.slice(0, 7) + '.csv');
	}

	// LE FICHIER DE COMMANDE de l'émetteur (Swile & consorts) : à déposer tel quel dans leur espace
	// admin, Nouvelle commande ▸ Importer via fichier.
	//
	// On vérifie AVANT de produire : un fichier auquel il manque une date de naissance ou une
	// adresse est rejeté à l'import, et cela se découvre à la date de créditement — trop tard pour
	// recommencer. Les fiches incomplètes sont donc nommées, avec ce qui leur manque.
	onExportSwile(): void {
		if (!this.report) return;
		const people = this.beneficiaries();
		const gaps = swileGaps(this.report, people);
		if (gaps.length) {
			const list = gaps.slice(0, 12)
				.map(g => '<li><b>' + g.name + '</b> — ' + g.missing.join(', ') + '</li>').join('');
			Swal.fire({
				title: 'Fiches incomplètes',
				html: '<p style="text-align:left">L\'émetteur rejettera ces bénéficiaires :</p>'
					+ '<ul style="text-align:left">' + list + '</ul>'
					+ (gaps.length > 12 ? '<p>… et ' + (gaps.length - 12) + ' autre(s).</p>' : '')
					+ '<p style="text-align:left">Ces informations se saisissent sur la fiche salarié '
					+ '(<b>Identité de paie</b> et <b>Contact</b>).</p>',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Exporter quand même',
				cancelButtonText: 'Compléter les fiches',
			}).then(res => {
				if (res.isConfirmed) this.emitSwile(people);
			});
			return;
		}
		this.emitSwile(people);
	}

	private emitSwile(people: Map<number, MealVoucherBeneficiary>): void {
		if (!this.report) return;
		this.download(buildSwileCsv(this.report, people), 'commande-titres-' + this.from.slice(0, 7) + '.csv');
	}

	// L'identité telle que l'émetteur la demande, composée depuis la fiche salarié et le c_user lié.
	private beneficiaries(): Map<number, MealVoucherBeneficiary> {
		const out = new Map<number, MealVoucherBeneficiary>();
		for (const e of this.employees) {
			const u = e.lnk_user ?? this.users.find(x => x.id === e.id_user);
			const d = e.data ?? {};
			out.set(e.id, {
				id_employee: e.id,
				firstname: u?.firstname ?? '',
				lastname: u?.lastname ?? '',
				// Sexe et naissance : COLONNES de la fiche depuis le 2026-07-27 (ex-clés de data).
				gender: e.gender ?? '',
				email: d.email ?? '',
				birth_date: e.birth_date ?? '',
				// Le matricule, c'est le CODE PAIE de la fiche : un seul identifiant de
				// rapprochement, celui que le comptable connaît déjà.
				matricule: d.pay?.payroll_code ?? '',
				street: d.address?.street ?? '',
				zip: d.address?.zip ?? '',
				city: d.address?.city ?? '',
				// Carte physique : le cas par défaut est la carte déjà en main (renouvellement
				// mensuel du crédit). En commander une se décide par personne, sur sa fiche.
				physical_card: d.pay?.meal_physical_card === true,
			});
		}
		return out;
	}

	// BOM UTF-8 : sans lui, Excel français affiche « Salarié » en « SalariÃ© ».
	private download(csv: string, filename: string): void {
		const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();
		URL.revokeObjectURL(url);
	}
}
